import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useNavigate, useParams } from 'react-router-dom'
import { z } from 'zod'
import { ApiError } from '../api/client'
import {
  useCreateEvent,
  useEvent,
  useUpdateEvent,
  type EventFormValues,
} from '../features/events/api'
import { formatDate, usePreviewDateChange, type DateChangePreview } from '../features/plans/api'
import { useUsers } from '../features/users/api'

// Mirrors the server's rules so the obvious mistakes are caught before a round
// trip. The server revalidates regardless - this is convenience, not control.
const schema = z
  .object({
    name: z.string().trim().min(1, 'Name is required').max(256),
    accountManagerUserId: z.string().min(1, 'An account manager is required'),
    code: z.string().trim().min(1, 'Code is required').max(64),
    startDate: z.string().min(1, 'Start date is required'),
    endDate: z.string().min(1, 'End date is required'),
    venueName: z.string().max(256).optional(),
    venueCity: z.string().max(128).optional(),
    venueTimeZoneId: z.string().max(64).optional(),
    notes: z.string().max(4000).optional(),
    externalCustomRegId: z.string().max(128).optional(),
  })
  .refine((values) => values.endDate >= values.startDate, {
    message: 'End date must not be before the start date',
    path: ['endDate'],
  })

type FormValues = z.infer<typeof schema>

const blankToNull = (value?: string) => (value && value.trim() !== '' ? value.trim() : null)

function toRequest(values: FormValues): EventFormValues {
  return {
    name: values.name.trim(),
    code: values.code.trim(),
    accountManagerUserId: values.accountManagerUserId,
    startDate: values.startDate,
    endDate: values.endDate,
    venueName: blankToNull(values.venueName),
    venueCity: blankToNull(values.venueCity),
    venueTimeZoneId: blankToNull(values.venueTimeZoneId),
    notes: blankToNull(values.notes),
    externalCustomRegId: blankToNull(values.externalCustomRegId),
  }
}

export function EventFormPage() {
  const { eventId } = useParams()
  const isEdit = Boolean(eventId)
  const navigate = useNavigate()
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [needsPlanConfirmation, setNeedsPlanConfirmation] = useState(false)
  const [preview, setPreview] = useState<DateChangePreview | null>(null)
  const previewDateChange = usePreviewDateChange(eventId ?? '')

  const { data: existing, isPending: isLoading } = useEvent(eventId)
  const { data: users } = useUsers()
  const create = useCreateEvent()
  const update = useUpdateEvent(eventId ?? '')

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { name: '', code: '', startDate: '', endDate: '', accountManagerUserId: '' },
  })

  useEffect(() => {
    if (existing) {
      reset({
        name: existing.name,
        code: existing.code,
        startDate: existing.startDate,
        endDate: existing.endDate,
        accountManagerUserId: existing.accountManagerUserId,
        venueName: existing.venueName ?? '',
        venueCity: existing.venueCity ?? '',
        venueTimeZoneId: existing.venueTimeZoneId ?? '',
        notes: existing.notes ?? '',
        externalCustomRegId: existing.externalCustomRegId ?? '',
      })
    }
  }, [existing, reset])

  const onSubmit = handleSubmit(async (values) => {
    setSubmitError(null)
    const request = toRequest(values)

    try {
      const saved = isEdit
        ? await update.mutateAsync({
            ...request,
            confirmPlanRecalculation: needsPlanConfirmation,
          })
        : await create.mutateAsync(request)

      navigate(`/events/${saved.id}`)
    } catch (error) {
      if (error instanceof ApiError) {
        // The server refuses to move a planned event's dates silently, because
        // it shifts milestone due dates and can re-fire reminders. Fetch exactly
        // what would change and show it before asking for confirmation.
        if (error.problem?.type?.endsWith('plan-recalculation-required')) {
          setNeedsPlanConfirmation(true)
          try {
            setPreview(
              await previewDateChange.mutateAsync({
                startDate: values.startDate,
                endDate: values.endDate,
              }),
            )
          } catch {
            setSubmitError(
              'This event has a plan. Moving its dates will shift milestone due dates. ' +
                'Submit again to confirm.',
            )
          }
          return
        }
        setSubmitError(error.detail)
        return
      }
      setSubmitError('Could not save the event.')
    }
  })

  if (isEdit && isLoading) {
    return <p>Loading event…</p>
  }

  return (
    <section>
      <h1>{isEdit ? 'Edit event' : 'New event'}</h1>

      {submitError ? (
        <p role="alert" className={needsPlanConfirmation ? 'warning' : 'error'}>
          {submitError}
        </p>
      ) : null}

      {preview ? (
        <div className="panel" role="alert">
          <h2>Moving these dates changes the plan</h2>
          <p>
            {preview.willMoveCount} milestone{preview.willMoveCount === 1 ? '' : 's'} will move
            {preview.unchangedCount > 0
              ? `, and ${preview.unchangedCount} will be left alone.`
              : '.'}
          </p>

          <div className="table-scroll">
            <table>
              <caption className="visually-hidden">Proposed due date changes</caption>
              <thead>
                <tr>
                  <th scope="col">Milestone</th>
                  <th scope="col">Current</th>
                  <th scope="col">Proposed</th>
                  <th scope="col">Note</th>
                </tr>
              </thead>
              <tbody>
                {preview.milestones.map((row) => (
                  <tr key={row.milestoneId} className={row.willMove ? undefined : 'row-inactive'}>
                    <td>{row.title}</td>
                    <td>{formatDate(row.currentDueDate)}</td>
                    <td>{row.willMove ? formatDate(row.proposedDueDate) : '—'}</td>
                    <td>{row.skippedReason ?? (row.willMove ? '' : 'Already on that date')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="hint">
            Completed milestones keep the date they were due, and pinned dates stay pinned.
          </p>
        </div>
      ) : null}

      <form onSubmit={onSubmit} className="form" noValidate>
        <div className="field">
          <label htmlFor="name">Name</label>
          <input id="name" {...register('name')} aria-invalid={Boolean(errors.name)} />
          {errors.name ? <span className="error">{errors.name.message}</span> : null}
        </div>

        <div className="field">
          <label htmlFor="accountManagerUserId">Account manager</label>
          <select
            id="accountManagerUserId"
            {...register('accountManagerUserId')}
            aria-invalid={Boolean(errors.accountManagerUserId)}
          >
            <option value="">Choose who is answerable for this event</option>
            {(users ?? [])
              .filter((user) => user.isActive)
              .map((user) => (
                <option key={user.id} value={user.id}>
                  {user.displayName}
                </option>
              ))}
          </select>
          <span className="hint">
            Holds overall responsibility and receives the weekly progress report. This says who is
            answerable, not what they can do.
          </span>
          {errors.accountManagerUserId ? (
            <span className="error">{errors.accountManagerUserId.message}</span>
          ) : null}
        </div>

        <div className="field">
          <label htmlFor="code">Code</label>
          <input id="code" {...register('code')} aria-invalid={Boolean(errors.code)} />
          {errors.code ? <span className="error">{errors.code.message}</span> : null}
        </div>

        <div className="field-row">
          <div className="field">
            <label htmlFor="startDate">Start date</label>
            <input id="startDate" type="date" {...register('startDate')} />
            {errors.startDate ? <span className="error">{errors.startDate.message}</span> : null}
          </div>

          <div className="field">
            <label htmlFor="endDate">End date</label>
            <input id="endDate" type="date" {...register('endDate')} />
            {errors.endDate ? <span className="error">{errors.endDate.message}</span> : null}
          </div>
        </div>

        <div className="field-row">
          <div className="field">
            <label htmlFor="venueName">Venue</label>
            <input id="venueName" {...register('venueName')} />
          </div>

          <div className="field">
            <label htmlFor="venueCity">City</label>
            <input id="venueCity" {...register('venueCity')} />
          </div>
        </div>

        <div className="field">
          <label htmlFor="venueTimeZoneId">Venue time zone</label>
          <input
            id="venueTimeZoneId"
            placeholder="America/Chicago"
            {...register('venueTimeZoneId')}
          />
          <span className="hint">
            Decides what “today” means when work is judged overdue. Leave blank to use the server’s
            zone.
          </span>
        </div>

        <div className="field">
          <label htmlFor="externalCustomRegId">CustomReg ID</label>
          <input id="externalCustomRegId" {...register('externalCustomRegId')} />
          <span className="hint">Optional. Reserved for a future CustomReg integration.</span>
        </div>

        <div className="field">
          <label htmlFor="notes">Notes</label>
          <textarea id="notes" rows={4} {...register('notes')} />
        </div>

        <div className="form-actions">
          <button type="submit" className="primary" disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : needsPlanConfirmation ? 'Confirm and save' : 'Save'}
          </button>
          <button type="button" onClick={() => navigate(-1)}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  )
}
